import React from "react";
import { Link } from "react-router-dom";
import { Star, Phone, ArrowRight, MapPin } from "lucide-react";
import { BUSINESS } from "@/data/content";
import { RoofAccent } from "@/components/RoofAccents";

export function Stars({ value = 5, size = 4 }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`w-${size} h-${size} ${i < value ? "fill-brand text-brand" : "text-ink-muted"}`} />
      ))}
    </div>
  );
}

export function TrustBar({ dark = false }) {
  return (
    <div className={`${dark ? "text-white border-white/20" : "text-ink border-ink-line"} border-y py-6`}>
      <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
        <div>
          <div className="font-heading text-3xl font-extrabold">180+</div>
          <div className="text-sm font-semibold mt-1">5-Star Reviews</div>
          <div className={`text-xs ${dark ? "text-white/60" : "text-ink-soft"}`}>Google, Angi &amp; Yelp verified</div>
        </div>
        <div>
          <div className="font-heading text-3xl font-extrabold">28+</div>
          <div className="text-sm font-semibold mt-1">Years Experience</div>
          <div className={`text-xs ${dark ? "text-white/60" : "text-ink-soft"}`}>Maciej Ogonowski, Owner - since 1998</div>
        </div>
        <div>
          <div className="font-heading text-3xl font-extrabold">1,500+</div>
          <div className="text-sm font-semibold mt-1">Roofs Installed</div>
          <div className={`text-xs ${dark ? "text-white/60" : "text-ink-soft"}`}>Across Oakland County</div>
        </div>
        <div>
          <div className="font-heading text-3xl font-extrabold">Master</div>
          <div className="text-sm font-semibold mt-1">Certified</div>
          <div className={`text-xs ${dark ? "text-white/60" : "text-ink-soft"}`}>Licensed & fully insured</div>
        </div>
      </div>
    </div>
  );
}

export function SectionHeading({ overline, title, sub, align = "left" }) {
  return (
    <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      {overline && <div className="overline mb-3">{overline}</div>}
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold tracking-tight text-ink">{title}</h2>
      {sub && <p className="mt-4 text-lg text-ink-soft leading-relaxed">{sub}</p>}
    </div>
  );
}

export function CTA({ to = "/get-estimate", primary = "Get Free Estimate", secondary = true, dark = false }) {
  return (
    <div className="flex flex-wrap gap-3 items-center">
      <Link to={to} className="btn-primary" data-testid="cta-primary">
        {primary} <ArrowRight className="w-4 h-4" />
      </Link>
      {secondary && (
        <a href={`tel:${BUSINESS.phoneTel}`} className={dark ? "btn-inverted" : "btn-secondary"} data-testid="cta-secondary-phone">
          <Phone className="w-4 h-4" /> {BUSINESS.phone}
        </a>
      )}
    </div>
  );
}

export function ReviewCard({ r }) {
  // Vary accent by rating position (hash of name char code)
  const variants = ["gable", "window", "door", "shingles", "vane", "skyline"];
  const v = variants[(r.name?.charCodeAt(0) || 0) % variants.length];
  return (
    <div className="group relative card-base p-6 h-full flex flex-col overflow-hidden hover:border-brand/30 transition-colors">
      <RoofAccent variant={v} position="bottom-right" size="md" intensity="soft" />
      <div className="flex items-center justify-between relative">
        <Stars value={r.rating} />
        {r.source && <span className="text-[10px] uppercase tracking-widest font-bold text-ink-soft">Verified · {r.source}</span>}
      </div>
      <p className="mt-4 text-ink leading-relaxed flex-1 relative">"{r.quote}"</p>
      <div className="mt-6 pt-4 border-t border-ink-line relative">
        <div className="font-semibold text-ink">{r.name}</div>
        <div className="text-xs text-ink-soft flex items-center gap-2 mt-1">
          <MapPin className="w-3 h-3" /> {r.city}, MI · {r.service} · {r.date}
        </div>
      </div>
    </div>
  );
}

export function FinalCTA() {
  return (
    <section className="group bg-bg-dark text-white relative overflow-hidden grain">
      {/* Large decorative house silhouette - brightens on hover of the section */}
      <svg aria-hidden="true" className="absolute -bottom-12 -right-8 w-[360px] md:w-[520px] h-auto opacity-[0.05] group-hover:opacity-[0.12] transition-opacity duration-700 pointer-events-none" viewBox="0 0 400 240" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M20 200 L200 70 L380 200" />
        <path d="M50 200 L50 230 L350 230 L350 200" />
        <rect x="160" y="160" width="80" height="70" />
        <path d="M160 190 L240 190 M200 160 L200 230" />
        <path d="M320 70 L320 50 L340 50 L340 90" />
      </svg>
      {/* Secondary skyline accent on the left */}
      <svg aria-hidden="true" className="absolute -bottom-4 left-0 w-[280px] md:w-[380px] h-auto opacity-[0.04] group-hover:opacity-[0.1] transition-opacity duration-700 pointer-events-none" viewBox="0 0 400 120" fill="none" stroke="currentColor" strokeWidth="1">
        <path d="M20 100 L60 60 L100 100" />
        <path d="M30 100 L30 120 L90 120 L90 100" />
        <path d="M120 100 L180 50 L240 100" />
        <path d="M130 100 L130 120 L230 120 L230 100" />
        <path d="M260 100 L300 70 L340 100" />
      </svg>
      <div className="container-x py-20 md:py-28 relative grid md:grid-cols-5 gap-10 items-center">
        <div className="md:col-span-3">
          <div className="overline text-brand">Ready to start?</div>
          <h2 className="text-4xl md:text-6xl font-heading font-extrabold tracking-tight mt-3 leading-[1.05]">
            Get a free, no-pressure<br />roof estimate in 24 hours.
          </h2>
          <p className="mt-5 text-white/80 text-lg max-w-xl leading-relaxed">
            Tell us about your home. We'll inspect, measure, and hand you a detailed written quote - with honest advice, not sales scripts.
          </p>
          <div className="mt-8"><CTA dark /></div>
        </div>
        <div className="md:col-span-2 md:pl-8">
          <div className="border-l-4 border-brand pl-6">
            <div className="overline">Or call</div>
            <a href={`tel:${BUSINESS.phoneTel}`} className="font-heading text-4xl md:text-5xl font-extrabold text-white hover:text-brand block mt-2">
              {BUSINESS.phone}
            </a>
            <div className="mt-4 text-white/70 text-sm leading-relaxed">
              Mon-Sat 7AM-7PM<br />
              24-hour emergency tarping across Oakland County
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
